// Pure functions extracted from token-manager.ts — refresh scheduling,
// backoff, auth headers, and HTTP response classification. Kept in a
// separate file so the heavy DOM-touching code in `token-manager.ts`
// stays focused and these helpers can be tested without jsdom.
//
// Moved here from cloud/dp/shell/src/logic/token-manager-utils.ts as
// part of consolidating the auth stack inside @zion/sdk-react.
import { SESSION_TRANSPORT_COOKIE, SESSION_TRANSPORT_HEADER } from '@zion/sdk-contracts';
/**
 * The one header that selects cookie transport, as a spreadable object.
 *
 * @returns `{ [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE }`, a fresh object each call so a
 *   caller may mutate it. Pure: no DOM, no storage, no fetch, which is what this file is for.
 *   Throws nothing.
 *
 * Only the two requests carrying a session credential set it: refresh and logout. `GET
 * /v1/auth/me`, `POST /v1/auth/app-token` and every caller-supplied request through
 * `createAuthFetch` do not, because they mint no session (D14). The shell builds the same two keys
 * in its own `api/client.ts` rather than importing this (default F9: the shared thing is the
 * constant pair in `@zion/sdk-contracts`, and a two-key object literal is not a contract shape).
 */
export function sessionTransportHeaders() {
    return { [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE };
}
/** Default buffer before expiry to trigger a refresh (60s). */
export const REFRESH_BUFFER_MS = 60_000;
/**
 * Maximum delay cap for exponential backoff (120s). It is also the ceiling of every wait that a
 * `Retry-After` header sets, in `classifyResponse` (issue #3699).
 */
export const MAX_BACKOFF_DELAY_SEC = 120;
/** Initial backoff delay for the first transient refresh failure (5s). */
export const INITIAL_BACKOFF_SEC = 5;
/**
 * Compute milliseconds until the next silent refresh should fire.
 * Returns null if already past the buffer (interceptor handles it).
 */
export function computeRefreshDelay(expiresAt, now, bufferMs = REFRESH_BUFFER_MS) {
    if (expiresAt === null)
        return null;
    const ms = expiresAt - now - bufferMs;
    return ms > 0 ? ms : null;
}
/**
 * Determine if a token needs immediate refresh (expired or within buffer).
 */
export function needsImmediateRefresh(expiresAt, now, bufferMs = REFRESH_BUFFER_MS) {
    if (expiresAt === null)
        return true;
    return now >= expiresAt - bufferMs;
}
/**
 * Compute the next exponential backoff delay for 503 retry.
 * Doubles each time, capped at maxDelaySec.
 */
export function computeBackoffDelay(currentDelaySec, maxDelaySec = MAX_BACKOFF_DELAY_SEC) {
    return Math.min(currentDelaySec * 2, maxDelaySec);
}
/**
 * Build a cache key for module deduplication: `appId@version`.
 */
export function buildModuleCacheKey(appId, version) {
    return `${appId}@${version}`;
}
/**
 * Determine whether an Authorization header should be injected.
 * Returns the header value or null if not needed.
 */
export function resolveAuthHeader(token, existingHeaders) {
    if (!token)
        return null;
    // If the caller already set Authorization, don't override
    if (existingHeaders) {
        const h = new Headers(existingHeaders);
        if (h.has('Authorization'))
            return null;
    }
    return `Bearer ${token}`;
}
/**
 * `431 Request Header Fields Too Large` (#2864). Node refuses a request whose
 * headers exceed `--max-http-header-size` (16 KiB by default) in its HTTP
 * parser, BEFORE any framework routes it — so a 431 carries no application
 * body, reaches no controller, and writes no URL-keyed access-log line.
 *
 * ⛔ IT IS NEITHER TRANSIENT NOR RETRYABLE, and every instinct the rest of this
 * file encodes points the wrong way on it. The oversized header is the
 * caller's own `Authorization: Bearer <access token>`, so a replay sends the
 * same bytes and earns the same refusal. A refresh makes it worse — a reissued
 * token is at least as large as the one it replaces. `POST /v1/auth/logout`
 * carries the bearer too, so signing out 431s as well, and a fresh login mints
 * a larger token still. The only remedy is a smaller token, which only the
 * platform can deliver.
 */
export const REQUEST_HEADER_TOO_LARGE_STATUS = 431;
/**
 * Parse a `Retry-After` header as whole seconds for a wait.
 *
 * @param retryAfterHeader the header value, an untrusted server string, or `null`.
 * @param fallbackSec the wait for a value that is absent, not a number, zero or negative.
 * @returns the parsed seconds, at most `MAX_BACKOFF_DELAY_SEC` (120). A value above the ceiling
 *   gives 120: a wait above 2147483647 ms makes `setTimeout` fire after 1 ms (issue #3699).
 */
function retryAfterWaitSec(retryAfterHeader, fallbackSec) {
    if (retryAfterHeader === null)
        return fallbackSec;
    const sec = parseInt(retryAfterHeader, 10);
    return Number.isFinite(sec) && sec > 0 ? Math.min(sec, MAX_BACKOFF_DELAY_SEC) : fallbackSec;
}
/** Cold-start `Retry-After` parse: a finite positive integer up to 120, else 1s. Unlike
 * the maintenance default (30s), a cold pod is expected back within seconds. */
function coldStartRetryAfterSec(retryAfterHeader) {
    return retryAfterWaitSec(retryAfterHeader, 1);
}
/**
 * Classify an HTTP response into a transport action. Cold-start is checked
 * BEFORE maintenance so the two `503` meanings never collide (PR1 — HTTP
 * cold-start contract, §5.B.2). The `retryAfterSec` of an action is at most
 * `MAX_BACKOFF_DELAY_SEC` (120), whatever the header says. Decision order:
 *   1. `431` → request_too_large.
 *   2. `x-keda-http-cold-start: true` OR `502` OR `504` → cold_start_retry.
 *   3. `401` → retry_with_refresh.
 *   4. `503` + Retry-After (and not cold-start) → maintenance (org migration).
 *   5. plain `503` (no Retry-After) → cold_start_retry (the edge contract).
 *   6. `2xx` → ok; everything else → pass_through.
 */
export function classifyResponse(status, retryAfterHeader, coldStartHeader) {
    // ⛔ FIRST, ABOVE COLD START, AND THE ORDER IS THE POINT. The KEDA
    // interceptor sits in front of core-api and stamps
    // `x-keda-http-cold-start: true` on what it forwards, so a 431 arriving
    // during a scale-from-zero would otherwise classify as `cold_start_retry`
    // and buy four backoff waits before surfacing the identical refusal — the
    // retry re-sends the same oversized header, byte for byte. See
    // {@link REQUEST_HEADER_TOO_LARGE_STATUS}.
    if (status === REQUEST_HEADER_TOO_LARGE_STATUS)
        return { type: 'request_too_large' };
    if (coldStartHeader === 'true' || status === 502 || status === 504) {
        return { type: 'cold_start_retry', retryAfterSec: coldStartRetryAfterSec(retryAfterHeader) };
    }
    if (status === 401)
        return { type: 'retry_with_refresh' };
    if (status === 503 && retryAfterHeader) {
        return { type: 'maintenance', retryAfterSec: retryAfterWaitSec(retryAfterHeader, 30) };
    }
    if (status === 503) {
        return { type: 'cold_start_retry', retryAfterSec: coldStartRetryAfterSec(retryAfterHeader) };
    }
    if (status >= 200 && status < 300)
        return { type: 'ok' };
    return { type: 'pass_through' };
}
/**
 * The signal that `fetch` obeys for one call, by the Fetch rule. The retry loops of
 * `createAuthFetch` and `coldStartAwareFetch` listen to it during each wait between two attempts.
 *
 * @param input the request target, as the caller handed it to `fetch`.
 * @param init the request options, or `undefined`.
 * @returns `init.signal` when it is not `undefined` (`null` means no signal and gives `null`).
 *   Else the signal of a `Request` input. Else `null`. Reads `init.signal` once. Throws nothing.
 */
export function callerSignalOf(input, init) {
    if (init?.signal !== undefined)
        return init.signal;
    return input instanceof Request ? input.signal : null;
}
/**
 * Start a wait of `ms` milliseconds that a caller abort and its owner can end early (issue
 * #3699). The maintenance loop of `createAuthFetch` keeps `wake` in its window, so the first
 * retry answer other than `503` ends every wait of the window at once.
 *
 * Every exit removes what the wait added: the timer and the abort listener go on the time, on a
 * wake and on an abort. An already aborted signal starts no timer and adds no listener.
 *
 * @param ms the length of the wait. A maintenance wait is at most 120 s. A cold-start wait is
 *   the larger of the backoff and a `Retry-After` of at most 120 s, and a caller backoff has no
 *   ceiling.
 * @param signal the caller's signal from {@link callerSignalOf}, or `null` for none.
 * @returns the wait. Its creation throws nothing.
 */
export function startWait(ms, signal) {
    let wake = () => undefined;
    const done = new Promise((resolve, reject) => {
        if (signal === null) {
            const timer = setTimeout(resolve, ms);
            wake = () => {
                clearTimeout(timer);
                resolve();
            };
            return;
        }
        if (signal.aborted) {
            reject(signal.reason);
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(signal.reason);
        };
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, { once: true });
        wake = () => {
            clearTimeout(timer);
            signal.removeEventListener('abort', onAbort);
            resolve();
        };
    });
    return { done, wake };
}
/**
 * Run an injected sleep, and end it at once when the caller's signal aborts (issue #3699). The
 * `sleep` option of `coldStartAwareFetch` is a seam this module cannot cancel, so an abort
 * rejects the returned promise and leaves the injected sleep to finish on its own.
 *
 * @param sleep the injected sleep. Its rejection reaches the caller as it is.
 * @param ms the length of the wait, passed to `sleep` unchanged.
 * @param signal the caller's signal from {@link callerSignalOf}, or `null` for none.
 * @returns a promise that settles as `sleep(ms)` does, or rejects with the signal's reason on an
 *   abort. An already aborted signal rejects at once and does not call `sleep`. The abort
 *   listener goes away on every exit.
 */
export function sleepUnlessAborted(sleep, ms, signal) {
    if (signal === null)
        return sleep(ms);
    if (signal.aborted)
        return Promise.reject(signal.reason);
    return new Promise((resolve, reject) => {
        const onAbort = () => {
            reject(signal.reason);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        void sleep(ms)
            .then(resolve, reject)
            .finally(() => {
            signal.removeEventListener('abort', onAbort);
        });
    });
}
//# sourceMappingURL=token-manager-utils.js.map